import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { validateWithServer } from '@/lib/validation';
import { debounce } from 'lodash';

interface ValidatedInputProps {
  id?: string;
  name?: string;
  type?: 'text' | 'email';
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean, message?: string) => void;
  validationType?: 'email' | 'displayName' | 'organizationName';
  required?: boolean;
  disabled?: boolean;
  className?: string;
  label?: string;
  showValidationIcon?: boolean;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
}

export const ValidatedInput = React.forwardRef<HTMLInputElement, ValidatedInputProps>(
  ({ 
    id,
    name,
    type = 'text',
    placeholder,
    value,
    onChange,
    onValidationChange,
    validationType,
    required = false,
    disabled = false,
    className,
    label,
    showValidationIcon = true,
    validateOnBlur = true,
    validateOnChange = false,
    ...props
  }, ref) => {
    const [validationState, setValidationState] = useState<{
      isValid: boolean | null;
      message?: string;
      isValidating: boolean;
    }>({ isValid: null, isValidating: false, message: undefined });

    const performValidation = useCallback(async (inputValue: string) => {
      if (!validationType || !inputValue.trim()) {
        setValidationState({ isValid: null, isValidating: false });
        onValidationChange?.(true);
        return;
      }

      setValidationState(prev => ({ ...prev, isValidating: true }));

      try {
        let result;
        switch (validationType) {
          case 'email':
            result = await validateWithServer.email(inputValue);
            break;
          case 'displayName':
            result = await validateWithServer.displayName(inputValue);
            break;
          case 'organizationName':
            result = await validateWithServer.organizationName(inputValue);
            break;
          default:
            result = { isValid: true };
        }

        setValidationState({
          isValid: result.isValid,
          message: result.message,
          isValidating: false,
        });
        onValidationChange?.(result.isValid, result.message);
      } catch (error) {
        setValidationState({
          isValid: false,
          message: 'Validation error occurred',
          isValidating: false,
        });
        onValidationChange?.(false, 'Validation error occurred');
      }
    }, [validationType, onValidationChange]);

    const debouncedValidation = useCallback(
      debounce(performValidation, 500),
      [performValidation]
    );

    useEffect(() => {
      if (validateOnChange && value) {
        debouncedValidation(value);
      }
      return () => {
        debouncedValidation.cancel();
      };
    }, [value, validateOnChange, debouncedValidation]);

    const handleBlur = () => {
      if (validateOnBlur && value) {
        performValidation(value);
      }
    };

    const getValidationIcon = () => {
      if (!showValidationIcon || validationState.isValid === null) return null;

      if (validationState.isValidating) {
        return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
      }

      if (validationState.isValid) {
        return <Check className="h-4 w-4 text-green-600" />;
      }

      return <X className="h-4 w-4 text-destructive" />;
    };

    const getInputClassName = () => {
      let baseClass = className || '';
      
      if (validationState.isValid === true) {
        baseClass += ' border-green-500 focus:border-green-500';
      } else if (validationState.isValid === false) {
        baseClass += ' border-destructive focus:border-destructive';
      }

      if (showValidationIcon && validationState.isValid !== null) {
        baseClass += ' pr-10';
      }

      return baseClass;
    };

    return (
      <div className="space-y-2">
        {label && (
          <Label htmlFor={id}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        
        <div className="relative">
          <Input
            ref={ref}
            id={id}
            name={name}
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={handleBlur}
            required={required}
            disabled={disabled}
            className={getInputClassName()}
            {...props}
          />
          
          {showValidationIcon && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {getValidationIcon()}
            </div>
          )}
        </div>

        {validationState.message && validationState.isValid === false && (
          <p className="text-xs text-destructive flex items-center gap-1">
            <X className="h-3 w-3 flex-shrink-0" />
            {validationState.message}
          </p>
        )}

        {validationState.message && validationState.isValid === true && (
          <p className="text-xs text-green-600 flex items-center gap-1">
            <Check className="h-3 w-3 flex-shrink-0" />
            {validationState.message}
          </p>
        )}
      </div>
    );
  }
);

ValidatedInput.displayName = 'ValidatedInput';